export function startAnimation(canvas, svgBase64) {
  canvas.addEventListener("click", () => {});

  let isHState = false; // Tracks whether the "H" state is active
  let toggleLock = false; // Prevents rapid toggling due to key repeat

  const ctx = canvas.getContext("2d");
  const parent = canvas.parentElement;
  canvas.width = parent.clientWidth;
  canvas.height = parent.clientHeight;

  let particlesArray = [];
  const particleSpacing = 10;

  const baseAsciiFontSize = 8;
  // const largeAsciiFontSize = 12;
  const movementDistance = 0.75;
  // const movementSpeed = 1500;
  const mouseRadius = 150;
  const clusteringStrength = 0.004;
  let fightingStrength = 0.0000000002;
  const randomMovementStrength = 0.0;
  const resetInterval = 50000;
  const syntaxColors = ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF"];

  const characters = Array.from({ length: 95 }, (_, i) =>
    String.fromCharCode(i + 32),
  );

  let dominantColor =
    syntaxColors[Math.floor(Math.random() * syntaxColors.length)];
  let lastResetTime = performance.now();

  const mouse = { x: null, y: null, radius: mouseRadius };

  canvas.addEventListener("mousemove", (event) => {
    mouse.x = event.offsetX;
    mouse.y = event.offsetY;
  });

  function animateHState(callback) {
    let progress = 0;
    const duration = 60; // Number of frames for the animation
    const step = 1 / duration;

    function moveToBasePosition() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesArray.forEach((particle) => {
        // Interpolate particle position toward the base position
        particle.x += (particle.baseX - particle.x) * step;
        particle.y += (particle.baseY - particle.y) * step;
        particle.draw();
      });

      progress += step;
      if (progress < 1) {
        requestAnimationFrame(moveToBasePosition);
      } else {
        console.log("H state animation completed");
        if (callback) callback(); // Call the callback after the animation finishes
      }
    }

    requestAnimationFrame(moveToBasePosition);
  }

  function toggleHState() {
    isHState = !isHState; // Toggle the H state
    console.log(isHState ? "H state active" : "H state deactivated");

    init().then(() => {
      particlesArray.forEach((particle) => {
        particle.x = Math.random() * canvas.width; // Reset to random position
        particle.y = Math.random() * canvas.height;
      });

      animateHState(() => {
        console.log("H state toggle completed");
      });
    });
  }

  window.addEventListener("keydown", (event) => {
    if (event.key.toLowerCase() === "h" && !toggleLock) {
      toggleLock = true; // Lock toggling until animation or logic completes
      toggleHState();
    }
  });

  window.addEventListener("keyup", (event) => {
    if (event.key.toLowerCase() === "h") {
      toggleLock = false; // Unlock toggling when key is released
    }
  });

  class Particle {
    constructor(x, y) {
      this.x = Math.random() * canvas.width;
      this.y = Math.random() * canvas.height;
      this.baseX = x;
      this.baseY = y;
      this.density = Math.random() * 15 + 5;
      this.color =
        syntaxColors[Math.floor(Math.random() * syntaxColors.length)];
      this.char = characters[Math.floor(Math.random() * characters.length)];
      this.offsetX = Math.random() * movementDistance - movementDistance / 2;
      this.offsetY = Math.random() * movementDistance - movementDistance / 2;
    }

    draw() {
      ctx.fillStyle = this.color;
      // Character size change is commented out
      // ctx.font = `${isHState ? largeAsciiFontSize : baseAsciiFontSize}px monospace`;
      ctx.font = `${baseAsciiFontSize}px monospace`; // Use base size
      ctx.fillText(this.char, this.x, this.y);
    }

    update(particles) {
      let dx = mouse.x - this.x;
      let dy = mouse.y - this.y;
      let distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < mouse.radius) {
        const forceX = dx / distance;
        const forceY = dy / distance;
        this.x -= forceX * this.density;
        this.y -= forceY * this.density;
      } else {
        this.x += (this.baseX - this.x) * 0.1 + this.offsetX * 0.5;
        this.y += (this.baseY - this.y) * 0.1 + this.offsetY * 0.5;
      }

      // Random movement to keep things dynamic
      this.x += (Math.random() - 0.5) * randomMovementStrength;
      this.y += (Math.random() - 0.5) * randomMovementStrength;

      // Gradually adopt the dominant color
      if (Math.random() < clusteringStrength) {
        this.color = dominantColor;
      }

      // Fight with neighbors for color dominance
      const neighbors = particles.filter(
        (p) =>
          Math.abs(p.x - this.x) < particleSpacing &&
          Math.abs(p.y - this.y) < particleSpacing,
      );

      neighbors.forEach((neighbor) => {
        if (Math.random() < fightingStrength) {
          this.color = neighbor.color;
        }
      });
    }
  }

  async function loadSVGFromHTML() {
    const svgData = atob(svgBase64);
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(svgData, "image/svg+xml");
    const paths = xmlDoc.getElementsByTagName("path");

    return Array.from(paths).map((path) => path.getAttribute("d"));
  }

  async function init() {
    const minScale = 0.4;
    const maxScale = 1;
    const minWidth = 300;
    const maxWidth = 700;

    let scaleFactor = maxScale;
    if (window.innerWidth > maxWidth) {
      scaleFactor = maxScale;
    } else if (window.innerWidth < minWidth) {
      scaleFactor = minScale;
    } else {
      scaleFactor =
        (window.innerWidth - minWidth) / (maxWidth - minWidth) + minScale;
    }

    particlesArray = [];
    const svgPaths = await loadSVGFromHTML();
    let allPositions = [];

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    // Calculate particle positions and track bounding box
    svgPaths.forEach((pathData) => {
      const path = new Path2D(pathData);
      for (let y = 0; y < canvas.height; y += particleSpacing) {
        for (let x = 0; x < canvas.width; x += particleSpacing) {
          if (
            ctx.isPointInPath(
              path,
              x * (1 / scaleFactor),
              y * (1 / scaleFactor),
            )
          ) {
            allPositions.push({ x, y });
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
          }
        }
      }
    });

    // Calculate centering offsets
    const offsetX = (canvas.width - (maxX - minX)) / 2 - minX;
    const offsetY = (canvas.height - (maxY - minY)) / 2 - minY;

    // Apply centering to particle positions
    allPositions.forEach((pos) => {
      particlesArray.push(new Particle(pos.x + offsetX, pos.y + offsetY));
    });
  }

  function animate() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let dominantColorCount = 0;

    particlesArray.forEach((particle) => {
      if (particle.color === dominantColor) {
        dominantColorCount++;
      }
      particle.update(particlesArray);
      particle.draw();
    });

    const currentTime = performance.now();

    // Check for winning condition
    if (
      dominantColorCount === particlesArray.length ||
      currentTime - lastResetTime > resetInterval
    ) {
      console.log("win!"); // Log "win!" when the color successfully wins

      // Update `dominantColor` to a different one
      let newColor;
      do {
        newColor =
          syntaxColors[Math.floor(Math.random() * syntaxColors.length)];
      } while (newColor === dominantColor);
      dominantColor = newColor;

      toggleHState(); // Automatically toggle H state on win
      lastResetTime = currentTime; // Update reset time
    }

    fightingStrength = Math.min(fightingStrength + 0.0001, 0.1);

    requestAnimationFrame(animate);
  }

  window.addEventListener("resize", () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    init();
  });

  init();
  animate();
}

export function startTextSwitching(element) {
  const sequence = [
    { className: "one in", duration: 500 },
    { className: "one", duration: 6000 },
    { className: "one out", duration: 500 },
    { className: "two in", duration: 500 },
    { className: "two", duration: 6000 },
    { className: "two out", duration: 500 },
    { className: "three in", duration: 500 },
    { className: "three", duration: 6000 },
    { className: "three out", duration: 500 },
  ];

  let index = 0;

  function changeClass() {
    element.className = sequence[index].className;
    setTimeout(() => {
      index = (index + 1) % sequence.length;
      changeClass();
    }, sequence[index].duration);
  }

  // Přidání počátečního zpoždění 1s
  setTimeout(changeClass, 200);
}
